import type { MemberMarketVM } from '@tv-casino/viewmodels/components/memberMarketModel.js';
import styles from './MemberMarket.module.css';
import { SparkLegend, Sparkline } from './Sparkline.js';
import { SuitIcon } from './SuitIcon.js';
import { GAME_GLYPHS } from '@tv-casino/viewmodels/components/memberHistoryModel.js';

const DIE = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];

type Tone = 'win' | 'loss' | 'even' | 'push';

const toneText = (tone: Tone): string =>
  (tone === 'win' ? styles.win : tone === 'loss' ? styles.loss : styles.even) ?? '';
const pillClass = (tone: Tone): string =>
  (tone === 'win' ? styles.pillWin : tone === 'loss' ? styles.pillLoss : styles.pillEven) ?? '';
const edgeClass = (tone: Tone): string =>
  (tone === 'win' ? styles.edgeWin : tone === 'loss' ? styles.edgeLoss : styles.edgeEven) ?? '';
const deskClass = (tone: Tone): string =>
  (tone === 'win' ? styles.deskWin : tone === 'loss' ? styles.deskLoss : styles.deskEven) ?? '';

/** The board reads at ten feet: four figures, not seven. */
const BOARD_METRICS = 4;

/**
 * One member's night as a trading desk: ticker quote, sparkline, market
 * metrics, the settled-round blotter and the raw chip tape. Pure render —
 * every label comes from `memberMarketModel`.
 */
export function MemberMarket({
  vm,
  onLoadMoreTrades,
  onLoadMoreLedger,
  tradesBusy = false,
  ledgerBusy = false,
  compact = false,
  quoteOnly = false,
  collapsible = false,
}: {
  vm: MemberMarketVM;
  onLoadMoreTrades?: () => void;
  onLoadMoreLedger?: () => void;
  tradesBusy?: boolean;
  ledgerBusy?: boolean;
  /** Tighter layout for the owner console drill-down. */
  compact?: boolean;
  /** TV board: quote + spark + metrics, no private tapes. */
  quoteOnly?: boolean;
  /** Phone lobby: trades and ledger fold behind their titles, closed by default. */
  collapsible?: boolean;
}) {
  if (!vm.ready) return null;

  const Tape = collapsible ? 'details' : 'div';
  const TapeTitle = collapsible ? 'summary' : 'p';

  const arrow = vm.changeTone === 'win' ? '▲' : vm.changeTone === 'loss' ? '▼' : '—';
  // The board reads at a glance: the story and four figures, not the stat
  // sheet. The phone, held a foot away, keeps every one of them.
  const metrics = quoteOnly ? vm.metrics.slice(0, BOARD_METRICS) : vm.metrics;
  // Nobody has moved a chip: one line beats a grid of dashes.
  const quiet = quoteOnly && vm.emptyLine !== null;

  return (
    <section
      className={[
        styles.desk,
        deskClass(vm.changeTone),
        compact ? styles.compact : '',
        quoteOnly ? styles.quoteOnly : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={`${vm.name} market desk`}
    >
      <div className={styles.quote}>
        <div className={styles.quoteHead}>
          <span className={styles.symbol}>{vm.symbol}</span>
          <span className={styles.quoteName}>{vm.name}</span>
        </div>
        <div className={styles.quoteRow}>
          <p className={styles.last}>
            {vm.lastLabel}
            <span className={styles.unit}>chips</span>
          </p>
          <p className={`${styles.change} ${pillClass(vm.changeTone)}`}>
            <span className={styles.arrow} aria-hidden>
              {arrow}
            </span>
            <span className={styles.changeFigures}>
              <span className={styles.changeChips}>{vm.changeLabel}</span>
              <span className={styles.pct}>{vm.changePctLabel}</span>
            </span>
          </p>
        </div>
        {quiet && <p className={styles.quiet}>{vm.emptyLine}</p>}
        <Sparkline
          points={vm.spark}
          runs={vm.sparkRuns}
          marks={vm.sparkMarks}
          single={vm.sparkSingle}
          baseline={vm.sparkBaseline}
          fill
          label="Balance sparkline"
        />
        <SparkLegend items={vm.sparkLegend} />
        {vm.highlights.length > 0 && (
          <ul className={styles.highlights} aria-label="Highlights">
            {vm.highlights.map((h) => (
              <li key={h.key} className={`${styles.highlight} ${edgeClass(h.tone)}`}>
                <span className={styles.highlightLabel}>{h.label}</span>
                <span className={styles.highlightValue}>
                  {h.game && (
                    <span className={`${styles.glyph} ${styles[h.game] ?? ''}`} aria-hidden>
                      {GAME_GLYPHS[h.game]}
                    </span>
                  )}
                  {h.value}
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* The OHLC strip is the stock vocabulary; a card with a story to
            tell leads with the story and lets the quotes keep the compact
            board. Nothing to highlight, the dashes carry the desk again. */}
        {!quiet && !(quoteOnly && vm.highlights.length > 0) && (
          <dl className={styles.ohlc}>
            <div>
              <dt>H</dt>
              <dd>{vm.highLabel}</dd>
            </div>
            <div>
              <dt>L</dt>
              <dd>{vm.lowLabel}</dd>
            </div>
            <div>
              <dt>Vol</dt>
              <dd>{vm.volumeLabel}</dd>
            </div>
            <div>
              <dt>DD</dt>
              <dd className={vm.drawdownLabel.startsWith('−') ? styles.loss : undefined}>
                {vm.drawdownLabel}
              </dd>
            </div>
          </dl>
        )}
      </div>

      {!quiet && metrics.length > 0 && (
        <dl className={styles.metrics}>
          {metrics.map((m) => (
            <div key={m.label}>
              <dt>{m.label}</dt>
              <dd
                className={
                  m.tone === 'win' ? styles.win : m.tone === 'loss' ? styles.loss : undefined
                }
              >
                {m.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {!quoteOnly &&
        (vm.emptyLine ? (
        <p className={styles.dim}>{vm.emptyLine}</p>
      ) : (
        <>
          <Tape className={styles.tape}>
            <TapeTitle className={styles.tapeTitle}>Trades</TapeTitle>
            {vm.trades.rows.length === 0 ? (
              <p className={styles.dim}>{tradesBusy ? 'Reading trades…' : 'No rounds settled yet.'}</p>
            ) : (
              <ul className={styles.trades} aria-label="Trades">
                {vm.trades.rows.map((round) => (
                  <li key={round.key} className={`${styles.trade} ${edgeClass(round.tone)}`}>
                    <div className={styles.tradeHead}>
                      <span className={`${styles.glyph} ${styles[round.gameType] ?? ''}`} aria-hidden>
                        {round.glyph}
                      </span>
                      <span className={styles.tradeGame}>{round.gameLabel}</span>
                      <span className={`${styles.pill} ${pillClass(round.tone)}`}>
                        {round.outcomeLabel}
                      </span>
                      <span className={styles.when}>{round.whenLabel}</span>
                      <span className={`${styles.tradeNet} ${toneText(round.tone)}`}>
                        {round.netLabel}
                      </span>
                    </div>

                    {(round.hands.length > 0 || round.dice || round.pocket) && (
                      <div className={styles.felt}>
                        {round.hands.map((hand) => (
                          <div key={hand.key} className={styles.hand}>
                            <span className={styles.handOwner}>{hand.ownerLabel}</span>
                            <span className={styles.cards}>
                              {hand.cards.map((card, i) => (
                                <span
                                  key={i}
                                  className={
                                    card.suit === 'H' || card.suit === 'D'
                                      ? `${styles.mini} ${styles.red}`
                                      : styles.mini
                                  }
                                >
                                  {card.rank}
                                  <SuitIcon suit={card.suit} />
                                </span>
                              ))}
                            </span>
                            {hand.label && <span className={styles.handLabel}>{hand.label}</span>}
                          </div>
                        ))}
                        {round.dice && (
                          <span
                            className={styles.dice}
                            role="img"
                            aria-label={`Last roll ${round.dice[0]} and ${round.dice[1]}`}
                          >
                            {DIE[round.dice[0] - 1]}
                            {DIE[round.dice[1] - 1]}
                          </span>
                        )}
                        {round.pocket && (
                          <span
                            className={`${styles.pocket} ${styles[`pocket_${round.pocket.color}`]}`}
                            aria-label={`Pocket ${round.pocket.label} ${round.pocket.color}`}
                          >
                            {round.pocket.label}
                          </span>
                        )}
                      </div>
                    )}

                    {round.note && !round.pocket && <p className={styles.note}>{round.note}</p>}

                    <div className={styles.flow} aria-label={`${round.stakeLabel}, ${round.returnLabel}`}>
                      <span className={styles.flowLabel}>In</span>
                      <span className={styles.track}>
                        <span
                          className={`${styles.fill} ${styles.fillStake}`}
                          style={{ width: `${round.stakeFrac * 100}%` }}
                        />
                      </span>
                      <span className={styles.flowChips}>{round.stakeChips}</span>
                      <span className={styles.flowLabel}>Out</span>
                      <span className={styles.track}>
                        <span
                          className={`${styles.fill} ${styles.fillReturn}`}
                          style={{ width: `${round.returnFrac * 100}%` }}
                        />
                      </span>
                      <span className={styles.flowChips}>{round.returnChips}</span>
                    </div>

                    {round.bets.length > 0 && (
                      <div className={styles.bets}>
                        {round.bets.map((bet, i) => (
                          <span
                            key={`${bet.label}-${i}`}
                            className={`${styles.chip} ${pillClass(bet.tone)}`}
                            title={bet.label}
                          >
                            <span className={styles.chipName}>{bet.name}</span>{' '}
                            <strong>{bet.netLabel}</strong>
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {vm.trades.hasMore && onLoadMoreTrades && (
              <button
                type="button"
                className={styles.more}
                disabled={tradesBusy}
                onClick={onLoadMoreTrades}
              >
                {tradesBusy ? 'Reading…' : 'Load more trades'}
              </button>
            )}
          </Tape>

          <Tape className={styles.tape}>
            <TapeTitle className={styles.tapeTitle}>Ledger</TapeTitle>
            {vm.ledgerGroups.length === 0 ? (
              <p className={styles.dim}>{ledgerBusy ? 'Reading tape…' : 'No movements yet.'}</p>
            ) : (
              <>
                <div className={styles.ledgerHead} aria-hidden>
                  <span>Move</span>
                  <span>Net</span>
                  <span>Stack</span>
                </div>
                <ul className={styles.ledger} aria-label="Ledger tape">
                  {vm.ledgerGroups.map((group) => (
                    <li
                      key={group.key}
                      className={`${styles.ledgerGroup} ${group.house ? styles.house : ''}`}
                    >
                      <div className={styles.groupHead}>
                        <span
                          className={`${styles.glyph} ${group.house ? styles.houseGlyph : ''}`}
                          aria-hidden
                        >
                          {group.glyph}
                        </span>
                        <span className={styles.groupTitle}>
                          {group.title}
                          <span className={styles.when}>{group.whenLabel}</span>
                        </span>
                        <span className={`${styles.groupNet} ${toneText(group.tone)}`}>
                          {group.netLabel}
                        </span>
                        <span className={styles.bal} aria-label={`Stack ${group.balanceLabel}`}>
                          {group.balanceLabel}
                        </span>
                      </div>
                      {!group.house && (
                        <div className={styles.lines}>
                          {group.lines.map((line) => (
                            <div key={line.key} className={styles.line}>
                              <span className={`${styles.lineIcon} ${toneText(line.tone)}`} aria-hidden>
                                {line.icon}
                              </span>
                              <span className={styles.kind}>{line.kindLabel}</span>
                              <span className={styles.meter} aria-hidden>
                                <span
                                  className={`${styles.meterFill} ${
                                    line.tone === 'loss' ? styles.meterOut : styles.meterIn
                                  }`}
                                  style={{ width: `${line.barFrac * 50}%` }}
                                />
                              </span>
                              <span className={`${styles.lineAmount} ${toneText(line.tone)}`}>
                                {line.amountLabel}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {vm.ledgerHasMore && onLoadMoreLedger && (
              <button
                type="button"
                className={styles.more}
                disabled={ledgerBusy}
                onClick={onLoadMoreLedger}
              >
                {ledgerBusy ? 'Reading…' : 'Load more tape'}
              </button>
            )}
          </Tape>
        </>
      ))}
    </section>
  );
}
